import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

/**
 * First-page thumbnail for a PDF, rendered in the browser.
 *
 * Three things keep this cheap, and all three matter:
 *  - pdfjs (~1MB with its worker) is imported lazily, so it never lands in the
 *    initial bundle; it loads the first time a PDF tile scrolls into view.
 *  - Rendered thumbnails are cached by URL, because the transaction timeline
 *    re-renders on pagination and would otherwise re-decode every time.
 *  - Nothing renders until the tile is actually visible.
 *
 * Falls back to a document icon on any failure — a thumbnail is a nicety, and
 * the tile is still clickable and still opens the real file.
 */

// url -> rendered data URL. Module-level so it survives component remounts.
const thumbCache = new Map<string, string>();
// In-flight renders, so two tiles for the same file don't both decode it.
const inFlight = new Map<string, Promise<string>>();

let pdfjsPromise: Promise<any> | null = null;

const loadPdfjs = async () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((pdfjs) => {
      // CRA's babel strips import.meta.url, so webpack never sees the
      // new Worker(new URL(...)) pattern and emits no worker chunk. The worker
      // is copied into public/ by the `copy-pdf-worker` script (wired to
      // prestart/prebuild) and served from there instead.
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
};

const renderThumb = async (url: string, pixels: number): Promise<string> => {
  const cached = thumbCache.get(url);
  if (cached) return cached;

  const existing = inFlight.get(url);
  if (existing) return existing;

  const job = (async () => {
    const pdfjs = await loadPdfjs();
    const doc = await pdfjs.getDocument({ url }).promise;
    try {
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      // Fit the page's long edge to the tile, at device resolution.
      const scale = (pixels * (window.devicePixelRatio || 1)) / Math.max(base.width, base.height);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      // White ground: a PDF page is transparent where nothing is drawn, which
      // would render as a black tile.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      const dataUrl = canvas.toDataURL('image/png');
      thumbCache.set(url, dataUrl);
      return dataUrl;
    } finally {
      // pdfjs holds the document open until told otherwise.
      doc.destroy();
    }
  })();

  inFlight.set(url, job);
  try {
    return await job;
  } finally {
    inFlight.delete(url);
  }
};

interface Props {
  url?: string;
  /** Tile edge in CSS pixels. */
  size?: number;
}

const PdfThumbnail = ({ url, size = 55 }: Props) => {
  const [src, setSrc] = useState<string | null>(url ? thumbCache.get(url) ?? null : null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Only render what someone can actually see.
  useEffect(() => {
    if (!ref.current || visible) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !url || src || failed) return;
    let cancelled = false;
    renderThumb(url, size)
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch((err) => {
        console.warn('PDF thumbnail failed, showing icon instead:', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, url, src, failed, size]);

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {src ? (
        <img
          src={src}
          alt="Document preview"
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : failed ? (
        <DescriptionOutlinedIcon sx={{ color: 'grey.500' }} />
      ) : (
        <CircularProgress size={16} thickness={5} sx={{ color: 'grey.400' }} />
      )}
    </Box>
  );
};

export default PdfThumbnail;
